import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { CURRENCIES } from '../../utils/currencies';
import { formatDateTime } from '../../utils/format';
import vnpfLogo from '../../assets/vnpf-logo.png';

// TS port of the source (Falah) app's pages/system/Settings.jsx, trimmed to
// the fields VNPF's Setting model actually has (see
// api/app/Http/Controllers/SettingController.php::update() for the exact
// validated field list). Falah's Mosque and Prayer Times sections don't
// exist here (VNPF has no mosque/prayer-times feature) and were dropped
// rather than carried over as dead UI.

// Intl.supportedValuesOf('timeZone') does not include the literal "UTC" in
// most engines, but it's a valid stored value, so it must always be selectable.
const TIMEZONES = [
  'UTC',
  ...(typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : ['Pacific/Efate', 'Australia/Sydney', 'Europe/London']),
];

const SECTIONS = [
  { value: 'branding', label: 'Branding' },
  { value: 'email_providers', label: 'Email Providers' },
  { value: 'organization', label: 'Organization' },
  { value: 'prefixes', label: 'Prefixes' },
  { value: 'general', label: 'General' },
  { value: 'benefits', label: 'Benefit Payments' },
  { value: 'human_resources', label: 'Human Resources' },
  { value: 'gl_interface', label: 'General Ledger Interface' },
  { value: 'memberships', label: 'Memberships' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'penalties', label: 'Penalties & Surcharges' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'receipts', label: 'Receipts' },
  { value: 'sms_providers', label: 'SMS Providers' },
  { value: 'support', label: 'Support' },
  { value: 'validations', label: 'Validations' },
].sort((a, b) => a.label.localeCompare(b.label));

const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ICON_MAX_BYTES = 2 * 1024 * 1024; // 2MB, matches the backend limit
const ICON_MIN_DIM = 16;
const ICON_MAX_DIM = 1024;

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image.'));
    };
    img.src = url;
  });
}

interface SettingsForm {
  currency: string;
  timezone: string;
  app_name: string;
  theme_primary_color: string;
  theme_accent_color: string;
  procurement_quotation_threshold: number | string;
  procurement_payment_approval_threshold: number | string;
  receipt_comment: string;
  service_desk_url: string;
  prefix_quotations: string;
  prefix_invoices: string;
  prefix_receipts: string;
  prefix_requests: string;
  prefix_orders: string;
  prefix_payments: string;
  prefix_system_id: string;
  prefix_contribution_receipts: string;
  prefix_employees: string;
  prefix_positions: string;
  prefix_assets: string;
  prefix_investments: string;
  working_days_per_month: number | string;
  timesheet_approval_levels: number | string;
  leave_attachment_required_days: number | string;
  payroll_requires_attendance: boolean;
  staff_contribution_employer_id: number | '';
  leave_liability_chart_of_account_id: number | '';
  min_employee_contribution_rate: number | string;
  min_employer_contribution_rate: number | string;
  contribution_payment_due_days: number | string;
  retirement_account_percentage: number | string;
  investment_account_percentage: number | string;
  medisave_account_percentage: number | string;
  penalty_rate_type: string;
  penalty_rate_value: number | string;
  penalty_grace_period_days: number | string;
  penalty_payment_due_days: number | string;
  penalty_waiver_escalation_threshold: number | string;
  penalty_notice_delivery_method: string;
  penalty_auto_issue_notice: boolean;
  penalty_income_chart_of_account_id: number | '';
  prefix_penalty_notices: string;
  prefix_penalty_receipts: string;
  benefit_min_retirement_age: number | string;
  benefit_min_membership_months: number | string;
  benefit_cheque_dual_signature_threshold: number | string;
  benefit_withdrawal_chart_of_account_id: number | '';
  benefit_death_chart_of_account_id: number | '';
  benefit_suspense_chart_of_account_id: number | '';
  prefix_benefit_claims: string;
  prefix_benefit_cheques: string;
  prefix_benefit_batches: string;
  gl_cash_bank_chart_of_account_id: number | '';
  prefix_gl_batches: string;
  prefix_journal_entries: string;
  ppe_accounting_policy_note: string;
  mobile_phone_pattern: string;
  send_portal_login_email: boolean;
  org_name: string;
  org_registration_number: string;
  org_tax_id: string;
  org_phone: string;
  org_email: string;
  org_address_line1: string;
  org_address_line2: string;
  org_city: string;
  org_state: string;
  org_postal_code: string;
  org_country: string;
}

type FormErrors = Record<string, string[]>;

export function Settings() {
  // Settings loads asynchronously — mounting the form before it resolves
  // would freeze `form`'s initial state on ConfigContext's stub defaults,
  // since useState's initializer only ever runs once. Deferring the form's
  // own mount until real data has arrived guarantees `settings` below is
  // the real thing.
  const { loaded } = useConfig();
  if (!loaded) return null;

  return <SettingsFormPage />;
}

function SettingsFormPage() {
  const { settings, reload } = useConfig();
  const confirmAction = useConfirm();
  const [section, setSection] = useState(SECTIONS[0].value);
  const [form, setForm] = useState<SettingsForm>({
    currency: settings.currency,
    timezone: settings.timezone,
    app_name: settings.app_name,
    theme_primary_color: settings.theme_primary_color,
    theme_accent_color: settings.theme_accent_color,
    procurement_quotation_threshold: settings.procurement_quotation_threshold,
    procurement_payment_approval_threshold: settings.procurement_payment_approval_threshold,
    receipt_comment: settings.receipt_comment ?? '',
    service_desk_url: settings.service_desk_url ?? '',
    prefix_quotations: settings.prefix_quotations,
    prefix_invoices: settings.prefix_invoices,
    prefix_receipts: settings.prefix_receipts,
    prefix_requests: settings.prefix_requests,
    prefix_orders: settings.prefix_orders,
    prefix_payments: settings.prefix_payments,
    prefix_system_id: settings.prefix_system_id,
    prefix_contribution_receipts: settings.prefix_contribution_receipts,
    prefix_employees: settings.prefix_employees,
    prefix_positions: settings.prefix_positions,
    prefix_assets: settings.prefix_assets,
    prefix_investments: settings.prefix_investments,
    working_days_per_month: settings.working_days_per_month,
    timesheet_approval_levels: settings.timesheet_approval_levels,
    leave_attachment_required_days: settings.leave_attachment_required_days ?? '',
    payroll_requires_attendance: settings.payroll_requires_attendance,
    staff_contribution_employer_id: settings.staff_contribution_employer_id ?? '',
    leave_liability_chart_of_account_id: settings.leave_liability_chart_of_account_id ?? '',
    min_employee_contribution_rate: settings.min_employee_contribution_rate,
    min_employer_contribution_rate: settings.min_employer_contribution_rate,
    retirement_account_percentage: settings.retirement_account_percentage,
    investment_account_percentage: settings.investment_account_percentage,
    medisave_account_percentage: settings.medisave_account_percentage,
    contribution_payment_due_days: settings.contribution_payment_due_days,
    penalty_rate_type: settings.penalty_rate_type,
    penalty_rate_value: settings.penalty_rate_value,
    penalty_grace_period_days: settings.penalty_grace_period_days,
    penalty_payment_due_days: settings.penalty_payment_due_days,
    penalty_waiver_escalation_threshold: settings.penalty_waiver_escalation_threshold,
    penalty_notice_delivery_method: settings.penalty_notice_delivery_method,
    penalty_auto_issue_notice: settings.penalty_auto_issue_notice,
    penalty_income_chart_of_account_id: settings.penalty_income_chart_of_account_id ?? '',
    prefix_penalty_notices: settings.prefix_penalty_notices,
    prefix_penalty_receipts: settings.prefix_penalty_receipts,
    benefit_min_retirement_age: settings.benefit_min_retirement_age,
    benefit_min_membership_months: settings.benefit_min_membership_months,
    benefit_cheque_dual_signature_threshold: settings.benefit_cheque_dual_signature_threshold,
    benefit_withdrawal_chart_of_account_id: settings.benefit_withdrawal_chart_of_account_id ?? '',
    benefit_death_chart_of_account_id: settings.benefit_death_chart_of_account_id ?? '',
    benefit_suspense_chart_of_account_id: settings.benefit_suspense_chart_of_account_id ?? '',
    prefix_benefit_claims: settings.prefix_benefit_claims,
    prefix_benefit_cheques: settings.prefix_benefit_cheques,
    prefix_benefit_batches: settings.prefix_benefit_batches,
    gl_cash_bank_chart_of_account_id: settings.gl_cash_bank_chart_of_account_id ?? '',
    prefix_gl_batches: settings.prefix_gl_batches,
    prefix_journal_entries: settings.prefix_journal_entries,
    ppe_accounting_policy_note: settings.ppe_accounting_policy_note ?? '',
    mobile_phone_pattern: settings.mobile_phone_pattern,
    send_portal_login_email: settings.send_portal_login_email,
    org_name: settings.org_name ?? '',
    org_registration_number: settings.org_registration_number ?? '',
    org_tax_id: settings.org_tax_id ?? '',
    org_phone: settings.org_phone ?? '',
    org_email: settings.org_email ?? '',
    org_address_line1: settings.org_address_line1 ?? '',
    org_address_line2: settings.org_address_line2 ?? '',
    org_city: settings.org_city ?? '',
    org_state: settings.org_state ?? '',
    org_postal_code: settings.org_postal_code ?? '',
    org_country: settings.org_country ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [chartOfAccounts, setChartOfAccounts] = useState<{ id: number; code: string; name: string; account_type: string }[]>([]);
  useEffect(() => {
    api
      .get<{ id: number; code: string; name: string; account_type: string }[]>('/chart-of-accounts')
      .then((res) => setChartOfAccounts(res.data))
      .catch(() => setChartOfAccounts([]));
  }, []);

  const [employers, setEmployers] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    api
      .get<{ data: { id: number; name: string }[] }>('/employers', { params: { per_page: 100 } })
      .then((res) => setEmployers(res.data.data))
      .catch(() => setEmployers([]));
  }, []);

  const [iconError, setIconError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [orgLogoError, setOrgLogoError] = useState('');
  const [uploadingOrgLogo, setUploadingOrgLogo] = useState(false);
  const orgLogoInputRef = useRef<HTMLInputElement | null>(null);

  // Email Providers has its own save action, separate from the shared form
  // above — the "New value" API key field is always blank (the saved key is
  // never sent back to the client) and is never submitted with the main form.
  const [resendEnabled, setResendEnabled] = useState(settings.resend_enabled);
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendSaving, setResendSaving] = useState(false);
  const [resendSaved, setResendSaved] = useState(false);
  const [resendErrors, setResendErrors] = useState<FormErrors>({});
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState('');

  // Not a form submit — this card lives inside the page's outer <form> (the
  // shared Save button below), and HTML forbids a nested <form>, so this is
  // a plain button onClick instead, matching the icon-upload actions above.
  const saveEmailProvider = async () => {
    setResendSaving(true);
    setResendErrors({});
    setResendSaved(false);
    setTestEmailResult('');
    try {
      await api.put('/settings/email-provider', {
        resend_enabled: resendEnabled,
        resend_api_key: resendApiKey,
      });
      await reload();
      setResendApiKey('');
      setResendSaved(true);
    } catch (err) {
      setResendErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setResendSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    setTestEmailResult('');
    try {
      const res = await api.post<{ email: string }>('/settings/email-provider/test');
      setTestEmailResult(`Test email sent to ${res.data.email}.`);
    } catch (err) {
      setTestEmailResult(err instanceof ApiError ? err.message : 'Could not send the test email.');
    } finally {
      setTestingEmail(false);
    }
  };

  // SMS Providers — same shape as Email Providers above: its own save
  // action outside the shared form, "New value" fields always blank (the
  // saved key/sender ID are never sent back to the client).
  const [smsToEnabled, setSmsToEnabled] = useState(settings.sms_to_enabled);
  const [smsToApiKey, setSmsToApiKey] = useState('');
  const [smsToSenderId, setSmsToSenderId] = useState('');
  const [smsToSaving, setSmsToSaving] = useState(false);
  const [smsToSaved, setSmsToSaved] = useState(false);
  const [smsToErrors, setSmsToErrors] = useState<FormErrors>({});

  const saveSmsProvider = async () => {
    setSmsToSaving(true);
    setSmsToErrors({});
    setSmsToSaved(false);
    try {
      await api.put('/settings/sms-provider', {
        sms_to_enabled: smsToEnabled,
        sms_to_api_key: smsToApiKey,
        sms_to_sender_id: smsToSenderId,
      });
      await reload();
      setSmsToApiKey('');
      setSmsToSenderId('');
      setSmsToSaved(true);
    } catch (err) {
      setSmsToErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSmsToSaving(false);
    }
  };

  const set = (key: keyof SettingsForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setSaved(false);
    setForm({ ...form, [key]: e.target.value });
  };

  const setChecked = (key: keyof SettingsForm) => (e: ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm({ ...form, [key]: e.target.checked });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      await api.put('/settings', form);
      await reload();
      setSaved(true);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSaving(false);
    }
  };

  const pickIcon = () => fileInputRef.current?.click();

  const onIconSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setIconError('');

    if (!ICON_TYPES.includes(file.type)) {
      setIconError('Please choose a PNG, JPG, or WEBP image.');
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      setIconError('Image must be 2MB or smaller.');
      return;
    }
    try {
      const { width, height } = await readImageDimensions(file);
      if (width < ICON_MIN_DIM || height < ICON_MIN_DIM || width > ICON_MAX_DIM || height > ICON_MAX_DIM) {
        setIconError(`Image dimensions must be between ${ICON_MIN_DIM}×${ICON_MIN_DIM} and ${ICON_MAX_DIM}×${ICON_MAX_DIM}px.`);
        return;
      }
    } catch {
      setIconError('That file could not be read as an image.');
      return;
    }

    const data = new FormData();
    data.append('icon', file);
    setUploading(true);
    try {
      await api.post('/settings/icon', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await reload();
    } catch (err) {
      setIconError(
        err instanceof ApiError ? err.fieldError('icon') ?? err.message : 'Upload failed.'
      );
    } finally {
      setUploading(false);
    }
  };

  const resetIcon = async () => {
    if (!(await confirmAction('Reset to the default icon?', { confirmLabel: 'Reset' }))) return;
    setUploading(true);
    setIconError('');
    try {
      await api.delete('/settings/icon');
      await reload();
    } catch {
      setIconError('Could not reset the icon.');
    } finally {
      setUploading(false);
    }
  };

  const pickOrgLogo = () => orgLogoInputRef.current?.click();

  const onOrgLogoSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setOrgLogoError('');

    if (!ICON_TYPES.includes(file.type)) {
      setOrgLogoError('Please choose a PNG, JPG, or WEBP image.');
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      setOrgLogoError('Image must be 2MB or smaller.');
      return;
    }
    try {
      const { width, height } = await readImageDimensions(file);
      if (width < ICON_MIN_DIM || height < ICON_MIN_DIM || width > ICON_MAX_DIM || height > ICON_MAX_DIM) {
        setOrgLogoError(`Image dimensions must be between ${ICON_MIN_DIM}×${ICON_MIN_DIM} and ${ICON_MAX_DIM}×${ICON_MAX_DIM}px.`);
        return;
      }
    } catch {
      setOrgLogoError('That file could not be read as an image.');
      return;
    }

    const data = new FormData();
    data.append('logo', file);
    setUploadingOrgLogo(true);
    try {
      await api.post('/settings/organization-logo', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await reload();
    } catch (err) {
      setOrgLogoError(err instanceof ApiError ? err.fieldError('logo') ?? err.message : 'Upload failed.');
    } finally {
      setUploadingOrgLogo(false);
    }
  };

  const removeOrgLogo = async () => {
    if (!(await confirmAction('Remove the organization logo?', { confirmLabel: 'Remove' }))) return;
    setUploadingOrgLogo(true);
    setOrgLogoError('');
    try {
      await api.delete('/settings/organization-logo');
      await reload();
    } catch {
      setOrgLogoError('Could not remove the logo.');
    } finally {
      setUploadingOrgLogo(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Settings</h1>
      </header>
      <p className="muted">Application-wide defaults used across the portal.</p>

      <div className="toolbar">
        <select value={section} onChange={(e) => setSection(e.target.value)}>
          {SECTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={save}>
        {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
        {saved && <div className="alert alert-success">Settings saved.</div>}

        {section === 'branding' && (
          <div className="card">
            <h2>Branding</h2>
            <div className="form-grid">
              <label className="span-2">
                Application Name
                <input value={form.app_name} onChange={set('app_name')} required maxLength={100} />
                {errors.app_name && <small className="error">{errors.app_name[0]}</small>}
              </label>
              <label>
                Primary Color
                <div className="input-with-button">
                  <input
                    type="color"
                    value={form.theme_primary_color}
                    onChange={set('theme_primary_color')}
                    required
                    style={{ flex: '0 0 48px', padding: 4 }}
                  />
                  <input
                    value={form.theme_primary_color}
                    onChange={set('theme_primary_color')}
                    required
                    maxLength={7}
                    pattern="#[0-9A-Fa-f]{6}"
                  />
                </div>
                {errors.theme_primary_color && <small className="error">{errors.theme_primary_color[0]}</small>}
                <small className="muted">Buttons, links, and highlights throughout the app.</small>
              </label>
              <label>
                Accent Color
                <div className="input-with-button">
                  <input
                    type="color"
                    value={form.theme_accent_color}
                    onChange={set('theme_accent_color')}
                    required
                    style={{ flex: '0 0 48px', padding: 4 }}
                  />
                  <input
                    value={form.theme_accent_color}
                    onChange={set('theme_accent_color')}
                    required
                    maxLength={7}
                    pattern="#[0-9A-Fa-f]{6}"
                  />
                </div>
                {errors.theme_accent_color && <small className="error">{errors.theme_accent_color[0]}</small>}
                <small className="muted">Secondary badges and warning highlights.</small>
              </label>
            </div>
            <div className="icon-upload">
              <img src={settings.app_icon_url ?? vnpfLogo} alt="" className="icon-upload-preview" />
              <div className="icon-upload-actions">
                <button type="button" className="btn btn-ghost" onClick={pickIcon} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload icon'}
                </button>
                {settings.app_icon_url && (
                  <button type="button" className="btn btn-danger-ghost" onClick={resetIcon} disabled={uploading}>
                    Reset to default
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onIconSelected}
              hidden
            />
            <small className="muted">
              PNG, JPG, or WEBP · max 2MB · {ICON_MIN_DIM}–{ICON_MAX_DIM}px square recommended.
            </small>
            {iconError && <div className="alert alert-error icon-upload-error">{iconError}</div>}
          </div>
        )}

        {section === 'organization' && (
          <div className="card">
            <h2>Organization</h2>
            <p className="muted">
              The registered entity's details, shown on print documents (invoices, receipts, letters). Separate from
              the Application Name/icon above, which is just this portal's own branding. Every field here is
              optional.
            </p>
            <div className="form-grid">
              <label className="span-2">
                Organization Name
                <input value={form.org_name} onChange={set('org_name')} maxLength={255} />
                {errors.org_name && <small className="error">{errors.org_name[0]}</small>}
              </label>
              <label>
                Registration Number
                <input value={form.org_registration_number} onChange={set('org_registration_number')} maxLength={100} />
                {errors.org_registration_number && <small className="error">{errors.org_registration_number[0]}</small>}
              </label>
              <label>
                Tax ID
                <input value={form.org_tax_id} onChange={set('org_tax_id')} maxLength={100} />
                {errors.org_tax_id && <small className="error">{errors.org_tax_id[0]}</small>}
              </label>
              <label>
                Phone
                <input value={form.org_phone} onChange={set('org_phone')} maxLength={30} />
                {errors.org_phone && <small className="error">{errors.org_phone[0]}</small>}
              </label>
              <label>
                Email
                <input type="email" value={form.org_email} onChange={set('org_email')} maxLength={255} />
                {errors.org_email && <small className="error">{errors.org_email[0]}</small>}
              </label>
              <label className="span-2">
                Address Street 1
                <input value={form.org_address_line1} onChange={set('org_address_line1')} maxLength={255} />
                {errors.org_address_line1 && <small className="error">{errors.org_address_line1[0]}</small>}
              </label>
              <label className="span-2">
                Address Street 2
                <input value={form.org_address_line2} onChange={set('org_address_line2')} maxLength={255} />
                {errors.org_address_line2 && <small className="error">{errors.org_address_line2[0]}</small>}
              </label>
              <label>
                City
                <input value={form.org_city} onChange={set('org_city')} maxLength={100} />
                {errors.org_city && <small className="error">{errors.org_city[0]}</small>}
              </label>
              <label>
                State/Province
                <input value={form.org_state} onChange={set('org_state')} maxLength={100} />
                {errors.org_state && <small className="error">{errors.org_state[0]}</small>}
              </label>
              <label>
                Zip/Postal Code
                <input value={form.org_postal_code} onChange={set('org_postal_code')} maxLength={30} />
                {errors.org_postal_code && <small className="error">{errors.org_postal_code[0]}</small>}
              </label>
              <label>
                Country
                <input value={form.org_country} onChange={set('org_country')} maxLength={100} />
                {errors.org_country && <small className="error">{errors.org_country[0]}</small>}
              </label>
            </div>
            <div className="icon-upload">
              <img src={settings.org_logo_url ?? vnpfLogo} alt="" className="icon-upload-preview" />
              <div className="icon-upload-actions">
                <button type="button" className="btn btn-ghost" onClick={pickOrgLogo} disabled={uploadingOrgLogo}>
                  {uploadingOrgLogo ? 'Uploading…' : 'Upload logo'}
                </button>
                {settings.org_logo_url && (
                  <button
                    type="button"
                    className="btn btn-danger-ghost"
                    onClick={removeOrgLogo}
                    disabled={uploadingOrgLogo}
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
            <input
              ref={orgLogoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onOrgLogoSelected}
              hidden
            />
            <small className="muted">
              PNG, JPG, or WEBP · max 2MB · {ICON_MIN_DIM}–{ICON_MAX_DIM}px square recommended.
            </small>
            {orgLogoError && <div className="alert alert-error icon-upload-error">{orgLogoError}</div>}
          </div>
        )}

        {section === 'email_providers' && (
          <div className="card">
            <div className="card-header">
              <h2>Resend {resendEnabled && <span className="badge badge-active">Active</span>}</h2>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={resendEnabled}
                  onChange={(e) => {
                    setResendSaved(false);
                    setResendEnabled(e.target.checked);
                  }}
                />
                <span className="toggle-switch-slider" />
              </label>
            </div>
            {resendErrors.general && <div className="alert alert-error">{resendErrors.general[0]}</div>}
            {resendSaved && <div className="alert alert-success">Email provider saved.</div>}
            <div className="form-grid">
              <label className="span-2">
                API Key *
                <input
                  type="password"
                  value={resendApiKey}
                  onChange={(e) => {
                    setResendSaved(false);
                    setResendApiKey(e.target.value);
                  }}
                  placeholder="New value"
                  maxLength={255}
                />
                <small className="muted">Your Resend API key, used as a bearer token.</small>
                {settings.resend_api_key_set && settings.resend_api_key_set_at && (
                  <small className="muted">
                    Configured — last updated on {formatDateTime(settings.resend_api_key_set_at)}
                  </small>
                )}
                {resendErrors.resend_api_key && <small className="error">{resendErrors.resend_api_key[0]}</small>}
              </label>
            </div>
            {testEmailResult && <p className="muted">{testEmailResult}</p>}
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={saveEmailProvider} disabled={resendSaving}>
                {resendSaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={sendTestEmail} disabled={testingEmail}>
                {testingEmail ? 'Sending…' : 'Send Test Email'}
              </button>
            </div>
          </div>
        )}

        {section === 'sms_providers' && (
          <div className="card">
            <div className="card-header">
              <h2>SMS.to {smsToEnabled && <span className="badge badge-active">Active</span>}</h2>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={smsToEnabled}
                  onChange={(e) => {
                    setSmsToSaved(false);
                    setSmsToEnabled(e.target.checked);
                  }}
                />
                <span className="toggle-switch-slider" />
              </label>
            </div>
            {smsToErrors.general && <div className="alert alert-error">{smsToErrors.general[0]}</div>}
            {smsToSaved && <div className="alert alert-success">SMS provider saved.</div>}
            <div className="form-grid">
              <label className="span-2">
                API Key *
                <input
                  type="password"
                  value={smsToApiKey}
                  onChange={(e) => {
                    setSmsToSaved(false);
                    setSmsToApiKey(e.target.value);
                  }}
                  placeholder="New value"
                  maxLength={255}
                />
                <small className="muted">Your SMS.to API key, used as a bearer token.</small>
                {settings.sms_to_api_key_set && settings.sms_to_api_key_set_at && (
                  <small className="muted">
                    Configured — last updated by {settings.sms_to_api_key_set_by} on{' '}
                    {formatDateTime(settings.sms_to_api_key_set_at)}
                  </small>
                )}
                {smsToErrors.sms_to_api_key && <small className="error">{smsToErrors.sms_to_api_key[0]}</small>}
              </label>
              <label className="span-2">
                Sender ID *
                <input
                  value={smsToSenderId}
                  onChange={(e) => {
                    setSmsToSaved(false);
                    setSmsToSenderId(e.target.value);
                  }}
                  placeholder="New value"
                  maxLength={255}
                />
                <small className="muted">The sender name recipients see (e.g. "System Name").</small>
                {settings.sms_to_sender_id_set && settings.sms_to_sender_id_set_at && (
                  <small className="muted">
                    Configured — last updated by {settings.sms_to_sender_id_set_by} on{' '}
                    {formatDateTime(settings.sms_to_sender_id_set_at)}
                  </small>
                )}
                {smsToErrors.sms_to_sender_id && <small className="error">{smsToErrors.sms_to_sender_id[0]}</small>}
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={saveSmsProvider} disabled={smsToSaving}>
                {smsToSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {section === 'memberships' && (
          <div className="card">
            <h2>Memberships</h2>
            <div className="form-grid">
              <label>
                Minimum employee contribution rate (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.min_employee_contribution_rate}
                  onChange={set('min_employee_contribution_rate')}
                  required
                />
                {errors.min_employee_contribution_rate && (
                  <small className="error">{errors.min_employee_contribution_rate[0]}</small>
                )}
              </label>
              <label>
                Minimum employer contribution rate (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.min_employer_contribution_rate}
                  onChange={set('min_employer_contribution_rate')}
                  required
                />
                {errors.min_employer_contribution_rate && (
                  <small className="error">{errors.min_employer_contribution_rate[0]}</small>
                )}
              </label>
              <small className="muted span-2">
                Contribution schedule lines are rejected below these statutory minimums.
              </small>
              <label>
                Contribution payment due (days after period end)
                <input
                  type="number"
                  min="0"
                  value={form.contribution_payment_due_days}
                  onChange={set('contribution_payment_due_days')}
                  required
                />
                {errors.contribution_payment_due_days && (
                  <small className="error">{errors.contribution_payment_due_days[0]}</small>
                )}
                <small className="muted">
                  Set when a submitted schedule's due date is calculated. Used by automatic penalty assessment.
                </small>
              </label>
              <label>
                Retirement Account (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.retirement_account_percentage}
                  onChange={set('retirement_account_percentage')}
                  required
                />
                {errors.retirement_account_percentage && (
                  <small className="error">{errors.retirement_account_percentage[0]}</small>
                )}
              </label>
              <label>
                Investment Account (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.investment_account_percentage}
                  onChange={set('investment_account_percentage')}
                  required
                />
                {errors.investment_account_percentage && (
                  <small className="error">{errors.investment_account_percentage[0]}</small>
                )}
              </label>
              <label>
                Medi-Save Account (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.medisave_account_percentage}
                  onChange={set('medisave_account_percentage')}
                  required
                />
                {errors.medisave_account_percentage && (
                  <small className="error">{errors.medisave_account_percentage[0]}</small>
                )}
              </label>
              <small className="muted span-2">
                Every contribution is split across a member's Retirement, Investment and Medi-Save accounts at these
                percentages — they must add up to 100%. Applies to future contributions only, not past ones already
                recorded.
              </small>
            </div>
          </div>
        )}

        {section === 'penalties' && (
          <div className="card">
            <h2>Penalties &amp; Surcharges</h2>
            <div className="form-grid">
              <label>
                Rate type
                <select value={form.penalty_rate_type} onChange={set('penalty_rate_type')}>
                  <option value="percentage_per_month">Percentage per month overdue</option>
                  <option value="flat_amount">Flat amount</option>
                </select>
              </label>
              <label>
                Rate value {form.penalty_rate_type === 'percentage_per_month' ? '(%)' : `(${settings.currency})`}
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.penalty_rate_value}
                  onChange={set('penalty_rate_value')}
                  required
                />
                {errors.penalty_rate_value && <small className="error">{errors.penalty_rate_value[0]}</small>}
                <small className="muted">
                  Illustrative default is 0 — automatic assessment is disabled until you confirm your fund's actual
                  statutory rate here.
                </small>
              </label>
              <label>
                Grace period (days)
                <input
                  type="number"
                  min="0"
                  value={form.penalty_grace_period_days}
                  onChange={set('penalty_grace_period_days')}
                  required
                />
                {errors.penalty_grace_period_days && (
                  <small className="error">{errors.penalty_grace_period_days[0]}</small>
                )}
              </label>
              <label>
                Penalty payment due (days after assessment)
                <input
                  type="number"
                  min="0"
                  value={form.penalty_payment_due_days}
                  onChange={set('penalty_payment_due_days')}
                  required
                />
                {errors.penalty_payment_due_days && (
                  <small className="error">{errors.penalty_payment_due_days[0]}</small>
                )}
              </label>
              <label>
                Waiver escalation threshold ({settings.currency})
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.penalty_waiver_escalation_threshold}
                  onChange={set('penalty_waiver_escalation_threshold')}
                  required
                />
                {errors.penalty_waiver_escalation_threshold && (
                  <small className="error">{errors.penalty_waiver_escalation_threshold[0]}</small>
                )}
                <small className="muted">
                  Illustrative default is 0 — every waiver requires senior/Manager-Board approval until you confirm
                  your fund's real delegation-of-authority limit here.
                </small>
              </label>
              <label>
                Notice delivery method
                <select value={form.penalty_notice_delivery_method} onChange={set('penalty_notice_delivery_method')}>
                  <option value="portal">Portal only</option>
                  <option value="email">Email</option>
                  <option value="letter">Letter (print manually)</option>
                </select>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.penalty_auto_issue_notice}
                  onChange={(e) => setForm({ ...form, penalty_auto_issue_notice: e.target.checked })}
                />
                Automatically issue a notice when a penalty is assessed
              </label>
              <label className="span-2">
                Penalty income GL account
                <select
                  value={form.penalty_income_chart_of_account_id}
                  onChange={(e) =>
                    setForm({ ...form, penalty_income_chart_of_account_id: e.target.value ? Number(e.target.value) : '' })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts
                    .filter((a) => a.account_type === 'revenue')
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                </select>
                {errors.penalty_income_chart_of_account_id && (
                  <small className="error">{errors.penalty_income_chart_of_account_id[0]}</small>
                )}
                <small className="muted">
                  Penalty revenue is receipted here — separately from the member contribution ledger.
                </small>
              </label>
            </div>
          </div>
        )}

        {section === 'benefits' && (
          <div className="card">
            <h2>Benefit Payments</h2>
            <div className="form-grid">
              <label>
                Minimum retirement age
                <input
                  type="number"
                  min="0"
                  value={form.benefit_min_retirement_age}
                  onChange={set('benefit_min_retirement_age')}
                  required
                />
                {errors.benefit_min_retirement_age && (
                  <small className="error">{errors.benefit_min_retirement_age[0]}</small>
                )}
                <small className="muted">
                  Illustrative default is 0 — every retirement claim is flagged for review until you confirm your
                  fund's actual minimum retirement age here.
                </small>
              </label>
              <label>
                Minimum membership period (months)
                <input
                  type="number"
                  min="0"
                  value={form.benefit_min_membership_months}
                  onChange={set('benefit_min_membership_months')}
                  required
                />
                {errors.benefit_min_membership_months && (
                  <small className="error">{errors.benefit_min_membership_months[0]}</small>
                )}
                <small className="muted">
                  Illustrative default is 0 — every claim is flagged for review until you confirm your fund's actual
                  minimum membership period here. Eligibility flags never block a claim; they are for officer review.
                </small>
              </label>
              <label className="span-2">
                Cheque dual-signature threshold ({settings.currency})
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.benefit_cheque_dual_signature_threshold}
                  onChange={set('benefit_cheque_dual_signature_threshold')}
                  required
                />
                {errors.benefit_cheque_dual_signature_threshold && (
                  <small className="error">{errors.benefit_cheque_dual_signature_threshold[0]}</small>
                )}
                <small className="muted">
                  Illustrative default is 0 — every cheque requires two signatures until you confirm your fund's real
                  single-signatory limit here.
                </small>
              </label>
              <label>
                Withdrawal benefits GL account
                <select
                  value={form.benefit_withdrawal_chart_of_account_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      benefit_withdrawal_chart_of_account_id: e.target.value ? Number(e.target.value) : '',
                    })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.benefit_withdrawal_chart_of_account_id && (
                  <small className="error">{errors.benefit_withdrawal_chart_of_account_id[0]}</small>
                )}
              </label>
              <label>
                Death benefits GL account
                <select
                  value={form.benefit_death_chart_of_account_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      benefit_death_chart_of_account_id: e.target.value ? Number(e.target.value) : '',
                    })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.benefit_death_chart_of_account_id && (
                  <small className="error">{errors.benefit_death_chart_of_account_id[0]}</small>
                )}
              </label>
              <label className="span-2">
                Unclaimed/returned benefits suspense GL account
                <select
                  value={form.benefit_suspense_chart_of_account_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      benefit_suspense_chart_of_account_id: e.target.value ? Number(e.target.value) : '',
                    })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.benefit_suspense_chart_of_account_id && (
                  <small className="error">{errors.benefit_suspense_chart_of_account_id[0]}</small>
                )}
                <small className="muted">
                  Used when a payment is returned unclaimed and marked for suspense pending correction.
                </small>
              </label>
            </div>
          </div>
        )}

        {section === 'gl_interface' && (
          <div className="card">
            <h2>General Ledger Interface</h2>
            <div className="form-grid">
              <label className="span-2">
                Cash/bank GL account
                <select
                  value={form.gl_cash_bank_chart_of_account_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      gl_cash_bank_chart_of_account_id: e.target.value ? Number(e.target.value) : '',
                    })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.gl_cash_bank_chart_of_account_id && (
                  <small className="error">{errors.gl_cash_bank_chart_of_account_id[0]}</small>
                )}
                <small className="muted">
                  Not set by default — the daily GL batch generator refuses to run until this is configured. Serves
                  as the automatic contra side of every automated transaction type (see System &gt; GL Account
                  Mappings).
                </small>
              </label>
              <label className="span-2">
                PPE accounting policy note
                <textarea
                  rows={4}
                  value={form.ppe_accounting_policy_note}
                  onChange={set('ppe_accounting_policy_note')}
                />
                <small className="muted">
                  Printed as a footer on the Fixed Asset Register and Depreciation Schedule PDF exports — measurement
                  basis, depreciation method, and useful lives by category.
                </small>
                {errors.ppe_accounting_policy_note && (
                  <small className="error">{errors.ppe_accounting_policy_note[0]}</small>
                )}
              </label>
            </div>
          </div>
        )}

        {section === 'procurement' && (
          <div className="card">
            <h2>Procurement</h2>
            <div className="form-grid">
              <label className="span-2">
                Quotation threshold ({settings.currency})
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.procurement_quotation_threshold}
                  onChange={set('procurement_quotation_threshold')}
                  required
                />
                <small className="muted">
                  Purchase requests at or above this amount require 3 quotations and a second, different approver.
                </small>
                {errors.procurement_quotation_threshold && (
                  <small className="error">{errors.procurement_quotation_threshold[0]}</small>
                )}
              </label>
              <label className="span-2">
                Payment approval threshold ({settings.currency})
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.procurement_payment_approval_threshold}
                  onChange={set('procurement_payment_approval_threshold')}
                  required
                />
                <small className="muted">
                  Payment vouchers at or above this amount require a second, different approver before they can be paid.
                </small>
                {errors.procurement_payment_approval_threshold && (
                  <small className="error">{errors.procurement_payment_approval_threshold[0]}</small>
                )}
              </label>
            </div>
          </div>
        )}

        {section === 'prefixes' && (
          <div className="card">
            <h2>Prefixes</h2>
            <div className="form-grid">
              <label>
                Quotations
                <input value={form.prefix_quotations} onChange={set('prefix_quotations')} required maxLength={10} />
                {errors.prefix_quotations && <small className="error">{errors.prefix_quotations[0]}</small>}
              </label>
              <label>
                Invoices
                <input value={form.prefix_invoices} onChange={set('prefix_invoices')} required maxLength={10} />
                {errors.prefix_invoices && <small className="error">{errors.prefix_invoices[0]}</small>}
              </label>
              <label>
                Receipts
                <input value={form.prefix_receipts} onChange={set('prefix_receipts')} required maxLength={10} />
                {errors.prefix_receipts && <small className="error">{errors.prefix_receipts[0]}</small>}
              </label>
              <label>
                Requests
                <input value={form.prefix_requests} onChange={set('prefix_requests')} required maxLength={10} />
                {errors.prefix_requests && <small className="error">{errors.prefix_requests[0]}</small>}
              </label>
              <label>
                Orders
                <input value={form.prefix_orders} onChange={set('prefix_orders')} required maxLength={10} />
                {errors.prefix_orders && <small className="error">{errors.prefix_orders[0]}</small>}
              </label>
              <label>
                Payments
                <input value={form.prefix_payments} onChange={set('prefix_payments')} required maxLength={10} />
                {errors.prefix_payments && <small className="error">{errors.prefix_payments[0]}</small>}
              </label>
              <label>
                Contribution Receipts
                <input
                  value={form.prefix_contribution_receipts}
                  onChange={set('prefix_contribution_receipts')}
                  required
                  maxLength={10}
                />
                {errors.prefix_contribution_receipts && (
                  <small className="error">{errors.prefix_contribution_receipts[0]}</small>
                )}
              </label>
              <label>
                Penalty Notices
                <input
                  value={form.prefix_penalty_notices}
                  onChange={set('prefix_penalty_notices')}
                  required
                  maxLength={10}
                />
                {errors.prefix_penalty_notices && <small className="error">{errors.prefix_penalty_notices[0]}</small>}
              </label>
              <label>
                Penalty Receipts
                <input
                  value={form.prefix_penalty_receipts}
                  onChange={set('prefix_penalty_receipts')}
                  required
                  maxLength={10}
                />
                {errors.prefix_penalty_receipts && (
                  <small className="error">{errors.prefix_penalty_receipts[0]}</small>
                )}
              </label>
              <label>
                Benefit Claims
                <input
                  value={form.prefix_benefit_claims}
                  onChange={set('prefix_benefit_claims')}
                  required
                  maxLength={10}
                />
                {errors.prefix_benefit_claims && <small className="error">{errors.prefix_benefit_claims[0]}</small>}
              </label>
              <label>
                Benefit Cheques
                <input
                  value={form.prefix_benefit_cheques}
                  onChange={set('prefix_benefit_cheques')}
                  required
                  maxLength={10}
                />
                {errors.prefix_benefit_cheques && <small className="error">{errors.prefix_benefit_cheques[0]}</small>}
              </label>
              <label>
                Benefit Payment Batches
                <input
                  value={form.prefix_benefit_batches}
                  onChange={set('prefix_benefit_batches')}
                  required
                  maxLength={10}
                />
                {errors.prefix_benefit_batches && <small className="error">{errors.prefix_benefit_batches[0]}</small>}
              </label>
              <label>
                GL Batches
                <input value={form.prefix_gl_batches} onChange={set('prefix_gl_batches')} required maxLength={10} />
                {errors.prefix_gl_batches && <small className="error">{errors.prefix_gl_batches[0]}</small>}
              </label>
              <label>
                Journal Entries
                <input
                  value={form.prefix_journal_entries}
                  onChange={set('prefix_journal_entries')}
                  required
                  maxLength={10}
                />
                {errors.prefix_journal_entries && <small className="error">{errors.prefix_journal_entries[0]}</small>}
              </label>
              <label>
                System ID
                <input value={form.prefix_system_id} onChange={set('prefix_system_id')} required maxLength={10} />
                <small className="muted">Used for each user's unique ID under System &gt; Users.</small>
                {errors.prefix_system_id && <small className="error">{errors.prefix_system_id[0]}</small>}
              </label>
              <label>
                Employees
                <input value={form.prefix_employees} onChange={set('prefix_employees')} required maxLength={10} />
                <small className="muted">Used for each employee's auto-generated Employee Code.</small>
                {errors.prefix_employees && <small className="error">{errors.prefix_employees[0]}</small>}
              </label>
              <label>
                Positions
                <input value={form.prefix_positions} onChange={set('prefix_positions')} required maxLength={10} />
                <small className="muted">Used for each position's auto-generated Position Code.</small>
                {errors.prefix_positions && <small className="error">{errors.prefix_positions[0]}</small>}
              </label>
              <label>
                Assets
                <input value={form.prefix_assets} onChange={set('prefix_assets')} required maxLength={10} />
                <small className="muted">Used for each asset's auto-generated Asset Tag.</small>
                {errors.prefix_assets && <small className="error">{errors.prefix_assets[0]}</small>}
              </label>
              <label>
                Investments
                <input value={form.prefix_investments} onChange={set('prefix_investments')} required maxLength={10} />
                <small className="muted">Used for each investment's auto-generated reference code.</small>
                {errors.prefix_investments && <small className="error">{errors.prefix_investments[0]}</small>}
              </label>
              <small className="muted span-2">Used as the prefix when generating each document's reference number.</small>
            </div>
          </div>
        )}

        {section === 'receipts' && (
          <div className="card">
            <h2>Receipts</h2>
            <div className="form-grid">
              <label className="span-2">
                Comment
                <input value={form.receipt_comment} onChange={set('receipt_comment')} maxLength={255} />
                <small className="muted">Shown at the bottom of every receipt.</small>
                {errors.receipt_comment && <small className="error">{errors.receipt_comment[0]}</small>}
              </label>
            </div>
          </div>
        )}

        {section === 'general' && (
          <div className="card">
            <h2>General</h2>
            <div className="form-grid">
              <label className="span-2">
                Currency
                <select value={form.currency} onChange={set('currency')}>
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {errors.currency && <small className="error">{errors.currency[0]}</small>}
              </label>
              <label className="span-2">
                Timezone
                <select value={form.timezone} onChange={set('timezone')}>
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                {errors.timezone && <small className="error">{errors.timezone[0]}</small>}
              </label>
            </div>
          </div>
        )}

        {section === 'human_resources' && (
          <div className="card">
            <h2>Human Resources</h2>
            <div className="form-grid">
              <label>
                Working days per month
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="31"
                  value={form.working_days_per_month}
                  onChange={set('working_days_per_month')}
                  required
                />
                <small className="muted">
                  Used with each position's work shift hours/day to derive an employee's hourly rate from their base
                  amount (for valuing overtime).
                </small>
                {errors.working_days_per_month && <small className="error">{errors.working_days_per_month[0]}</small>}
              </label>
              <label>
                Timesheet Approval Levels
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="10"
                  value={form.timesheet_approval_levels}
                  onChange={set('timesheet_approval_levels')}
                  required
                />
                <small className="muted">
                  How many managers must approve a timesheet entry, in order — 1: manager, 2: manager's manager, etc. 0
                  means no approval is required; the entry is approved automatically. Unlike Leave, Overtime, and
                  Performance, this applies globally since timesheet entries have no type or position to scope a
                  policy by.
                </small>
                {errors.timesheet_approval_levels && <small className="error">{errors.timesheet_approval_levels[0]}</small>}
              </label>
              <label>
                Sick Leave Attachment Required After (days)
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="365"
                  placeholder="No requirement"
                  value={form.leave_attachment_required_days}
                  onChange={set('leave_attachment_required_days')}
                />
                <small className="muted">
                  A sick leave request longer than this many days must have a supporting document attached before it
                  can be submitted. Leave blank for no requirement.
                </small>
                {errors.leave_attachment_required_days && (
                  <small className="error">{errors.leave_attachment_required_days[0]}</small>
                )}
              </label>
              <label className="span-2 checkbox-label">
                <input
                  type="checkbox"
                  checked={form.payroll_requires_attendance}
                  onChange={setChecked('payroll_requires_attendance')}
                />
                Exclude employees with no recorded attendance from payroll
              </label>
              <small className="muted span-2">
                When generating a payroll run, an employee with neither a timesheet entry nor a clock in/out record
                anywhere in the period is left out of the run entirely, rather than paid in full on salary alone.
              </small>
              {errors.payroll_requires_attendance && (
                <small className="error span-2">{errors.payroll_requires_attendance[0]}</small>
              )}
              <label className="span-2">
                Staff Contribution Employer
                <select
                  value={form.staff_contribution_employer_id}
                  onChange={(e) =>
                    setForm({ ...form, staff_contribution_employer_id: e.target.value ? Number(e.target.value) : '' })
                  }
                >
                  <option value="">None</option>
                  {employers.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
                <small className="muted">
                  The Employer that VNPF's own staff's contributions are filed under. When set, finalizing a payroll
                  run creates or updates a draft Contribution Schedule for this employer and period, with one line
                  per paid employee who is also a fund Member.
                </small>
                {errors.staff_contribution_employer_id && (
                  <small className="error">{errors.staff_contribution_employer_id[0]}</small>
                )}
              </label>
              <label className="span-2">
                Leave Liability GL Account
                <select
                  value={form.leave_liability_chart_of_account_id}
                  onChange={(e) =>
                    setForm({ ...form, leave_liability_chart_of_account_id: e.target.value ? Number(e.target.value) : '' })
                  }
                >
                  <option value="">None</option>
                  {chartOfAccounts
                    .filter((a) => a.account_type === 'liability')
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                </select>
                {errors.leave_liability_chart_of_account_id && (
                  <small className="error">{errors.leave_liability_chart_of_account_id[0]}</small>
                )}
                <small className="muted">
                  On the 1st of each month, an adjusting journal entry automatically corrects this account's balance
                  to the value of every active employee's unused Annual leave (posted with no dual-control review —
                  see the leave:post-liability-adjustment scheduled job).
                </small>
              </label>
            </div>
          </div>
        )}

        {section === 'support' && (
          <div className="card">
            <h2>Support</h2>
            <div className="form-grid">
              <label className="span-2">
                Service Desk Link
                <input value={form.service_desk_url} onChange={set('service_desk_url')} maxLength={255} />
                <small className="muted">Shown as a link on the Support page for staff to raise a ticket.</small>
                {errors.service_desk_url && <small className="error">{errors.service_desk_url[0]}</small>}
              </label>
            </div>
          </div>
        )}

        {section === 'validations' && (
          <div className="card">
            <h2>Validations</h2>
            <div className="form-grid">
              <label className="span-2">
                Mobile Phone Validation Pattern
                <input
                  value={form.mobile_phone_pattern}
                  onChange={set('mobile_phone_pattern')}
                  required
                  maxLength={255}
                />
                <small className="muted">
                  Regular expression used to validate mobile phone numbers (e.g. on Users/Suppliers).
                </small>
                {errors.mobile_phone_pattern && <small className="error">{errors.mobile_phone_pattern[0]}</small>}
              </label>
            </div>
          </div>
        )}

        {section === 'notifications' && (
          <div className="card">
            <h2>Notifications</h2>
            <div className="form-grid">
              <label className="span-2 checkbox-label">
                <input
                  type="checkbox"
                  checked={form.send_portal_login_email}
                  onChange={setChecked('send_portal_login_email')}
                />
                Email new portal accounts a password-reset link
              </label>
              <small className="muted span-2">
                When a Member is created with an email address, a portal login is automatically provisioned (or
                linked to an existing account with that email) and assigned the Member self-service role. With this
                on, they immediately get an email to set their own password; otherwise the account exists but needs
                an admin to trigger a password reset later.
              </small>
              {errors.send_portal_login_email && (
                <small className="error">{errors.send_portal_login_email[0]}</small>
              )}
            </div>
          </div>
        )}

        {/* Email Providers and SMS Providers each save through their own
            dedicated button/handler above (saveEmailProvider/saveSmsProvider)
            since they're independent integrations, not part of this shared
            form's fields — the generic Save below would otherwise double up
            with theirs and submit unrelated data. */}
        {section !== 'email_providers' && section !== 'sms_providers' && (
          <div className="form-actions">
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </form>
    </>
  );
}
